import {
  IonButton,
  IonContent,
  IonHeader,
  IonImg,
  IonPage,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonToolbar,
  useIonToast,
  IonRow,
  IonCol,
  IonInput,
} from '@ionic/react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { passwordRegex, emailRegex, passwordSubtitle } from 'common/include';
import { ICasino } from 'pages/AdminDashboard';
import { IState } from 'appRedux/createStore';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { AuthService } from 'services/auth';
import { CasinoService } from 'services/casino';
import { toastConfig } from 'config/ToastConfig';
import { filterCasinosForAccountManage } from './casinoAccountScope';

export const CasinoAccounts = () => {
  const adminGroups = (useSelector<IState>(
    (state) => state.app.persistedState.userInfo?.groups || []
  ) || []) as string[];
  const validationSchema = yup.object().shape({
    email: yup
      .string()
      .matches(emailRegex, 'Email address is not valid')
      .required('Email address is required'),
    password: yup
      .string()
      .matches(passwordRegex, passwordSubtitle)
      .required('Password is required'),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: { email: '', password: '' },
    resolver: yupResolver(validationSchema),
  });
  const [results, setResults] = useState<{ email: string; status: string; error?: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [casinos, setCasinos] = useState<ICasino[]>([]);
  const [casinoFilterValue, setCasinoFilterValue] = useState<number[]>([]);
  const service = new AuthService();
  const [presentToast] = useIonToast();

  // Helper to wrap observable in a Promise for async/await
  const createCasinoUser = (
    casinoKeys: (number | string)[],
    email: string,
    password: string
  ): Promise<{ success: boolean; error?: string }> => {
    return new Promise((resolve) => {
      service.CreateCasinoUser(email, password, casinoKeys).subscribe({
        next(data) {
          if (data?.message === 'Success') {
            toastConfig.color = 'success';
            toastConfig.message = `Account created for ${email}`;
            void presentToast(toastConfig);
            resolve({ success: true });
          } else {
            toastConfig.color = 'danger';
            toastConfig.message = data?.errorMsg || 'Unknown error';
            void presentToast(toastConfig);
            resolve({ success: false, error: data?.errorMsg || 'Unknown error' });
          }
        },
        error(err) {
          toastConfig.color = 'danger';
          toastConfig.message = err?.message || 'API error';
          void presentToast(toastConfig);
          resolve({ success: false, error: err?.message || 'API error' });
        },
      });
    });
  };

  const handleCreateAccount = async (data: { email: string; password: string }) => {
    setLoading(true);
    setResults([]);
    const email = data.email.trim();
    const pwd = data.password.trim();
    if (!email || !pwd) {
      setLoading(false);
      return;
    }
    const res = await createCasinoUser(casinoFilterValue, email, pwd);
    let allResults: { email: string; status: string; error?: string }[] = [];
    if (res.success) {
      allResults = [{ email, status: 'Created' }];
    } else {
      allResults = [{ email, status: 'Failed', error: res.error }];
    }
    setResults(allResults);
    if (allResults.every((r) => r.status === 'Created')) reset();
    setLoading(false);
  };

  useEffect(() => {
    const casinoSvc = new CasinoService();
    const sub = casinoSvc.GetAllCasinos().subscribe({
      next(casinoResponse) {
        if (casinoResponse) {
          // Follow-up: hardcoded casinoId !== 4 exclusion (unchanged).
          const isActiveCasinos = casinoResponse.DataSet.filter(
            (casinoInfo) => casinoInfo.isactive && casinoInfo.casinoId !== 4
          );
          const mapped = isActiveCasinos.map(({ casinoId, casinoName, shortName }) => ({
            casinoId,
            casinoName,
            shortName,
          }));
          // Only_* admins only see their casinos; unscoped (e.g. SlotcheckInternal) see all.
          const filteredCasinos = filterCasinosForAccountManage(mapped, adminGroups);
          setCasinos(filteredCasinos);
          setCasinoFilterValue((prev) =>
            prev.filter((id) => filteredCasinos.some((c) => c.casinoId === id))
          );
        }
      },
    });
    return () => sub.unsubscribe();
  }, [adminGroups]);

  return (
    <IonPage className="app-parent">
      <div className="slotcheck-header">
        <IonHeader className="small-header header-dark">
          <IonToolbar>
            <IonButton
              fill="clear"
              onClick={() => {
                history.back();
              }}
              className="icon-back"
              slot="start"
            >
              <IonImg src="assets/images/back-icon.svg" />
            </IonButton>
            <div className="logo header-title">Casino Accounts</div>
          </IonToolbar>
        </IonHeader>
      </div>
      <IonContent>
        <div className="content admin__content">
          <div className="slotcheck-admin-casino-account">
            <div style={{ padding: 16 }}>
              <h6>Select Casino</h6>
              <IonSelect
                className="custom-casino-select"
                value={casinoFilterValue}
                multiple={true}
                interface="alert"
                placeholder="Select Casino"
                style={{ background: '#fff', borderRadius: '5px' }}
                onIonChange={(e) => setCasinoFilterValue(e.detail.value)}
              >
                {casinos.map((eachCasino: ICasino, index: number) => (
                  <IonSelectOption key={index} value={eachCasino.casinoId}>
                    {eachCasino.shortName}
                  </IonSelectOption>
                ))}
              </IonSelect>

              <h6 style={{ marginTop: 24 }}>Create Casino Account</h6>

              <form onSubmit={handleSubmit(handleCreateAccount)}>
                <IonRow>
                  <IonCol size="12">
                    <span style={{ display: 'block', marginBottom: 4 }}>Email Address</span>
                    <Controller
                      name="email"
                      control={control}
                      render={({ field }) => (
                        <IonInput
                          value={field.value}
                          onIonInput={(e) =>
                            field.onChange((e.target as unknown as HTMLInputElement).value)
                          }
                          onBlur={field.onBlur}
                          type="email"
                          placeholder="Enter email address"
                          autocomplete="off"
                          style={{ background: '#fff', borderRadius: '5px' }}
                        />
                      )}
                    />
                    {errors.email && (
                      <span style={{ color: 'red', marginTop: 4, display: 'block' }}>
                        {errors.email.message}
                      </span>
                    )}
                  </IonCol>
                </IonRow>
                <IonRow>
                  <IonCol size="12">
                    <span style={{ display: 'block', marginBottom: 4 }}>Password</span>
                    <Controller
                      name="password"
                      control={control}
                      render={({ field }) => (
                        <IonInput
                          value={field.value}
                          onIonInput={(e) =>
                            field.onChange((e.target as unknown as HTMLInputElement).value)
                          }
                          onBlur={field.onBlur}
                          type="password"
                          placeholder="Enter password"
                          autocomplete="off"
                          style={{ background: '#fff', borderRadius: '5px' }}
                        />
                      )}
                    />
                    {errors.password && (
                      <span style={{ color: 'red', marginTop: 4, display: 'block' }}>
                        {errors.password.message}
                      </span>
                    )}
                  </IonCol>
                </IonRow>
                <IonButton
                  type="submit"
                  disabled={loading || casinoFilterValue.length === 0}
                  style={{ marginTop: 12, marginBottom: 12 }}
                >
                  {loading ? <IonSpinner name="dots" /> : 'Create Account'}
                </IonButton>
              </form>
              {/* {results.length > 0 && (
                <IonList>
                  {results.map((r) => (
                    <IonRow key={r.email} style={{ alignItems: 'center', marginBottom: 4 }}>
                      <IonCol size="12">
                        <span>
                          {r.email}: {r.status}
                          {r.error && <span style={{ color: 'red' }}> ({r.error})</span>}
                        </span>
                      </IonCol>
                    </IonRow>
                  ))}
                </IonList>
              )} */}
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};
